import React, { useCallback, useEffect, useState } from 'react';
import { Download, Link2, Mail, Plus } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import SearchableSelect from '../SearchableSelect';
import ReceiptModal from './ReceiptModal';
import { Btn, Modal, INPUT, LABEL, fmtDate, errMsg } from './ui';

const STATUS_TONE = {
  paid: 'bg-[#E6F4EA] text-[#2E844A]', partial: 'bg-[#FEF8E8] text-[#7A5300]', overdue: 'bg-[#FEF1EE] text-[#BA0517]',
  draft: 'bg-[#F3F2F2] text-[#706E6B]', void: 'bg-[#F3F2F2] text-[#706E6B]', cancelled: 'bg-[#F3F2F2] text-[#706E6B]',
  accepted: 'bg-[#E6F4EA] text-[#2E844A]', converted: 'bg-[#E6F4EA] text-[#2E844A]', rejected: 'bg-[#FEF1EE] text-[#BA0517]', expired: 'bg-[#F3F2F2] text-[#706E6B]',
};
const Pill = ({ s }) => <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase', STATUS_TONE[s] || 'bg-[#E0F2FE] text-[#0176D3]')}>{s?.replace('_', ' ')}</span>;

function Stat({ label, value, hint, tone }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-[#706E6B] font-semibold">{label}</div>
      <div className={cn('text-[18px] font-semibold tabular-nums truncate', tone === 'bad' && 'text-[#BA0517]', tone === 'good' && 'text-[#2E844A]')}>{value}</div>
      {hint && <div className="text-[11px] text-[#706E6B] truncate">{hint}</div>}
    </div>
  );
}

function Section({ title, count, children, empty }) {
  return (
    <section className="bg-white border border-[#D8DDE6] rounded-lg overflow-hidden">
      <h3 className="px-4 py-2.5 text-[13px] font-semibold border-b border-[#EEF1F6]">{title}{count ? <span className="text-[#706E6B] font-normal"> · {count}</span> : null}</h3>
      {count ? <div className="overflow-x-auto">{children}</div> : <p className="text-[12px] text-[#706E6B] px-4 py-4">{empty}</p>}
    </section>
  );
}
const T = ({ head, rows }) => (
  <table className="w-full text-[12px] sm:min-w-[32.5rem]">
    <thead className="text-[#706E6B]"><tr>{head.map((h, i) => <th key={h} className={cn('px-4 py-2 font-semibold', i >= head.length - 2 ? 'text-right' : 'text-left')}>{h}</th>)}</tr></thead>
    <tbody>{rows}</tbody>
  </table>
);

async function download(url, params, filename) {
  try {
    const res = await api.get(url, { params, responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = href; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (e) { alertService.error('Download failed', 'Statements need the Ledgers & Reports feature and a manager or admin role'); }
}

// Credit limit, hold and payment terms, editable by finance roles.
function CreditCard({ refParams, canManage }) {
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get('/crm/customer-finance/credit', { params: refParams }).then((r) => setC(r.data.credit)).catch(() => setC(null)), [JSON.stringify(refParams)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!c) return null;
  const money = (v) => formatCurrency(v || 0, c.currency);
  const used = c.creditLimit ? Math.min(100, Math.max(0, (c.outstanding / c.creditLimit) * 100)) : 0;
  const save = async () => {
    try {
      await api.put('/crm/customer-finance/credit', { ...refParams, creditLimit: edit.creditLimit === '' ? null : Number(edit.creditLimit), paymentTermsDays: edit.paymentTermsDays === '' ? null : Number(edit.paymentTermsDays), creditHold: edit.creditHold, creditHoldReason: edit.creditHoldReason });
      setEdit(null); load();
    } catch (e) { alertService.error('Not saved', errMsg(e)); }
  };
  return (
    <section className={cn('bg-white border rounded-lg p-4 flex flex-col gap-2', c.creditHold ? 'border-[#F8B6A8]' : 'border-[#D8DDE6]')}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold">Credit and terms {c.creditHold && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase bg-[#FEF1EE] text-[#BA0517]">On hold</span>}</h3>
        {canManage && <button type="button" className="text-[12px] text-[#0176D3] hover:underline" onClick={() => setEdit({ creditLimit: c.creditLimit ?? '', paymentTermsDays: c.ownTerms ? c.paymentTermsDays : '', creditHold: c.creditHold, creditHoldReason: c.creditHoldReason })}>Edit</button>}
      </div>
      <div className="text-[13px] flex flex-wrap gap-x-6 gap-y-1">
        <span>Limit: <strong>{c.creditLimit === null ? 'No limit' : money(c.creditLimit)}</strong></span>
        {c.creditLimit !== null && <span>Available: <strong className={c.available < 0 ? 'text-[#BA0517]' : ''}>{money(c.available)}</strong></span>}
        <span>Payment terms: <strong>{c.paymentTermsDays} days</strong>{c.ownTerms ? '' : ' (company default)'}</span>
      </div>
      {c.creditLimit ? <div className="h-2 bg-[#EEF1F6] rounded"><div className={cn('h-2 rounded', used >= 100 ? 'bg-[#BA0517]' : used >= 80 ? 'bg-[#FE9339]' : 'bg-[#2E844A]')} style={{ width: `${used}%` }} /></div> : null}
      {c.creditHold && c.creditHoldReason && <p className="text-[12px] text-[#8C1B0F]">{c.creditHoldReason}</p>}
      {edit && (
        <Modal title="Credit and payment terms" onClose={() => setEdit(null)} footer={<><Btn onClick={() => setEdit(null)}>Cancel</Btn><Btn primary onClick={save}>Save</Btn></>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={LABEL} htmlFor="cr-limit">Credit limit ({c.currency})</label><input id="cr-limit" type="number" min="0" step="0.01" className={INPUT} placeholder="No limit" value={edit.creditLimit} onChange={(e) => setEdit({ ...edit, creditLimit: e.target.value })} /></div>
            <div><label className={LABEL} htmlFor="cr-terms">Payment terms (days)</label><input id="cr-terms" type="number" min="0" max="365" className={INPUT} placeholder="Company default" value={edit.paymentTermsDays} onChange={(e) => setEdit({ ...edit, paymentTermsDays: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={edit.creditHold} onChange={(e) => setEdit({ ...edit, creditHold: e.target.checked })} />Put on credit hold — no new invoices can be sent</label>
          {edit.creditHold && <div><label className={LABEL} htmlFor="cr-reason">Reason</label><input id="cr-reason" className={INPUT} value={edit.creditHoldReason} onChange={(e) => setEdit({ ...edit, creditHoldReason: e.target.value })} placeholder="e.g. 90 days overdue, cheque bounced" /></div>}
          <p className="text-[12px] text-[#706E6B]">The limit is checked when an invoice is sent, against everything the customer already owes. Leave it empty for no limit.</p>
        </Modal>
      )}
    </section>
  );
}

// Everything financial about one customer, on their CRM page.
export function CustomerFinancePanel({ accountId, contactId, name, email }) {
  const ref = accountId ? { accountId } : { contactId };
  const [d, setD] = useState(null);
  const [receipt, setReceipt] = useState(false);
  const [portal, setPortal] = useState(null);
  const load = useCallback(() => {
    api.get('/crm/customer-finance/party', { params: ref }).then((r) => setD(r.data)).catch((e) => alertService.error('Could not load finance', errMsg(e)));
  }, [accountId, contactId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d?.canManage) return;
    api.get('/crm/collections/portal', { params: ref }).then((r) => setPortal(r.data.portal)).catch(() => setPortal(null));
  }, [d?.canManage]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="text-[13px] text-[#706E6B] p-4">Loading…</p>;
  const cur = d.currency;
  const money = (v, c = cur) => formatCurrency(v || 0, c);
  const b = d.base;

  const newPortal = async () => {
    try {
      const r = await api.post('/crm/collections/portal', ref);
      await navigator.clipboard?.writeText(r.data.portal.url).catch(() => {});
      alertService.success('Portal link copied', r.data.portal.url);
      setPortal({ active: true, expiresAt: r.data.portal.expiresAt });
    } catch (e) { alertService.error('Not created', errMsg(e)); }
  };
  const emailStatement = async () => {
    if (!(await alertService.confirm(`Email a statement to ${email || 'this customer'}?`, 'The PDF statement and a portal link are sent from your email account.', 'question', 'Send'))) return;
    try { const r = await api.post('/crm/collections/statements/send', { ...ref }); alertService.success('Statement sent', r.data.sentTo); } catch (e) { alertService.error('Not sent', errMsg(e)); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label="Outstanding" value={money(b.outstanding)} tone={b.outstanding > 0 ? undefined : 'good'} />
        <Stat label="Overdue" value={money(b.overdue)} tone={b.overdue > 0 ? 'bad' : undefined} hint={d.behaviour.oldestOverdueDays ? `Oldest ${d.behaviour.oldestOverdueDays} days late` : undefined} />
        <Stat label="Billed (all time)" value={money(b.billed)} hint={`${d.behaviour.invoices} invoice(s)`} />
        <Stat label="Paid" value={money(b.paid)} hint={d.behaviour.lastPayment ? `Last ${fmtDate(d.behaviour.lastPayment.date)}` : 'No payments yet'} />
        <Stat label="Advance held" value={money(b.advances)} hint="Not yet applied" />
        <Stat label="Avg. days to pay" value={d.behaviour.avgDaysToPay ?? '—'} />
      </div>
      <CreditCard refParams={ref} canManage={d.canManage} />
      {Object.keys(d.totals).length > 1 && (
        <p className="text-[12px] text-[#706E6B]">Totals above are in {cur}. By currency: {Object.entries(d.totals).map(([c, v]) => `${c} ${formatCurrency(v.outstanding, c)} outstanding`).join(' · ')}</p>
      )}
      {d.canManage && (
        <div className="flex flex-wrap gap-2">
          <Btn primary onClick={() => setReceipt(true)}><Plus size={13} className="inline -mt-0.5 mr-1" />Record payment</Btn>
          <Btn onClick={() => download('/crm/finance-reports/statement/pdf', ref, `Statement-${(name || 'customer').replace(/[^a-z0-9]+/gi, '-')}.pdf`)}><Download size={13} className="inline -mt-0.5 mr-1" />Statement</Btn>
          <Btn onClick={emailStatement}><Mail size={13} className="inline -mt-0.5 mr-1" />Email statement</Btn>
          <Btn onClick={newPortal}><Link2 size={13} className="inline -mt-0.5 mr-1" />{portal?.active ? 'New portal link' : 'Portal link'}</Btn>
          {portal?.active && <span className="text-[12px] text-[#706E6B] self-center">Portal active until {fmtDate(portal.expiresAt)}{portal.lastViewedAt ? ` · opened ${fmtDate(portal.lastViewedAt)}` : ' · not opened yet'}</span>}
        </div>
      )}

      <Section title="Invoices" count={d.invoices.length} empty="No invoices yet.">
        <T head={['Invoice', 'Issued', 'Due', 'Status', 'Total', 'Balance']} rows={d.invoices.map((i) => (
          <tr key={i._id} className="border-t border-[#F3F2F2]">
            <td className="px-4 py-2 font-semibold">{i.invoiceNumber}</td><td className="px-4 py-2">{fmtDate(i.issueDate)}</td>
            <td className={cn('px-4 py-2', i.overdue && 'text-[#BA0517] font-semibold')}>{fmtDate(i.dueDate)}</td>
            <td className="px-4 py-2"><Pill s={i.overdue ? 'overdue' : i.status} /></td>
            <td className="px-4 py-2 text-right tabular-nums">{money(i.totalAmount, i.currency)}</td>
            <td className="px-4 py-2 text-right tabular-nums font-semibold">{i.balance ? money(i.balance, i.currency) : '—'}</td>
          </tr>
        ))} />
      </Section>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Quotations" count={d.quotations.length} empty="No quotations yet.">
          <T head={['Quotation', 'Date', 'Status', 'Total']} rows={d.quotations.map((q) => (
            <tr key={q._id} className="border-t border-[#F3F2F2]"><td className="px-4 py-2 font-semibold">{q.quotationNumber}</td><td className="px-4 py-2">{fmtDate(q.issueDate)}</td><td className="px-4 py-2 text-right"><Pill s={q.status} /></td><td className="px-4 py-2 text-right tabular-nums">{money(q.totalAmount, q.currency)}</td></tr>
          ))} />
        </Section>
        <Section title="Payments received" count={d.receipts.length} empty="No payments recorded.">
          <T head={['Date', 'Method', 'For', 'Amount']} rows={d.receipts.map((r) => (
            <tr key={r._id} className={cn('border-t border-[#EEF1F6]', r.status === 'void' && 'opacity-50 line-through')}>
              <td className="px-4 py-2">{fmtDate(r.date)}</td>
              <td className="px-4 py-2 capitalize">{r.method?.replace('_', ' ')}{r.reference ? ` · ${r.reference}` : ''}{r.chequeStatus ? ` (${r.chequeStatus})` : ''}</td>
              <td className="px-4 py-2 text-right">{r.allocations.map((a) => a.invoiceId?.invoiceNumber).filter(Boolean).join(', ') || (r.advanceAmount ? 'Advance' : '—')}</td>
              <td className="px-4 py-2 text-right tabular-nums">{money(r.amount, r.currency)}{r.tdsAmount ? <span className="block text-[10px] text-[#706E6B]">incl. TDS {money(r.tdsAmount, r.currency)}</span> : null}</td>
            </tr>
          ))} />
        </Section>
        <Section title="Credit notes" count={d.creditNotes.length} empty="No credit notes.">
          <T head={['Credit note', 'Date', 'Against', 'Amount']} rows={d.creditNotes.map((c) => (
            <tr key={c._id} className={cn('border-t border-[#EEF1F6]', c.status === 'void' && 'opacity-50 line-through')}><td className="px-4 py-2 font-semibold">{c.creditNoteNumber}</td><td className="px-4 py-2">{fmtDate(c.date)}</td><td className="px-4 py-2 text-right">{c.invoiceId?.invoiceNumber || '—'}</td><td className="px-4 py-2 text-right tabular-nums">{money(c.totalAmount, c.currency)}</td></tr>
          ))} />
        </Section>
        <Section title="Advances held" count={d.advances.length} empty="No unapplied advances.">
          <T head={['Received', 'From', 'Amount', 'Left']} rows={d.advances.map((a) => (
            <tr key={a._id} className="border-t border-[#F3F2F2]"><td className="px-4 py-2">{fmtDate(a.date)}</td><td className="px-4 py-2 capitalize">{a.source}</td><td className="px-4 py-2 text-right tabular-nums">{money(a.amount, a.currency)}</td><td className="px-4 py-2 text-right tabular-nums font-semibold">{money(a.balance, a.currency)}</td></tr>
          ))} />
        </Section>
      </div>
      {receipt && (
        <ReceiptModal
          accounts={accountId ? [{ _id: accountId, name }] : []}
          contacts={contactId ? [{ _id: contactId, name }] : []}
          preset={ref}
          onClose={() => setReceipt(false)}
          onSaved={() => { setReceipt(false); alertService.success('Payment recorded'); load(); }}
        />
      )}
    </div>
  );
}

// Money on a deal: quoted, billed, collected, and profit after the vendor
// bills and expense claims tagged to it.
export function DealFinancePanel({ dealId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/crm/customer-finance/deal/${dealId}`).then((r) => setD(r.data)).catch((e) => alertService.error('Could not load finance', errMsg(e))); }, [dealId]);
  if (!d) return <p className="text-[13px] text-[#706E6B] p-4">Loading…</p>;
  const money = (v, c = d.currency) => formatCurrency(v || 0, c);
  const pct = (v) => (d.dealValue ? Math.min(100, Math.max(0, (v / d.dealValue) * 100)) : 0);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Deal value" value={money(d.dealValue)} />
        <Stat label="Quoted" value={money(d.quoted)} />
        <Stat label="Invoiced" value={money(d.billed)} hint={d.dealValue ? `${Math.round(pct(d.billed))}% of deal value` : undefined} />
        <Stat label="Collected" value={money(d.collected)} hint={d.outstanding ? `${money(d.outstanding)} outstanding` : 'Nothing outstanding'} tone={d.outstanding ? undefined : 'good'} />
      </div>
      {d.dealValue > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3">
          <div className="h-3 bg-[#EEF1F6] rounded relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#B9D8F5]" style={{ width: `${pct(d.billed)}%` }} />
            <div className="absolute inset-y-0 left-0 bg-[#0176D3]" style={{ width: `${pct(d.collected)}%` }} />
          </div>
          <div className="flex gap-4 mt-1.5 text-[11px] text-[#706E6B]"><span><span className="inline-block w-2 h-2 bg-[#0176D3] rounded-sm mr-1" />Collected</span><span><span className="inline-block w-2 h-2 bg-[#B9D8F5] rounded-sm mr-1" />Invoiced</span><span>of deal value incl. tax</span></div>
        </div>
      )}
      <section className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-col gap-3">
        <h3 className="text-[13px] font-semibold">Profitability</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Revenue (excl. tax)" value={money(d.revenue)} />
          <Stat label="Costs" value={money(d.costs)} hint={`${d.costLines.length} bill(s) and claim(s)`} />
          <Stat label="Profit" value={money(d.profit)} tone={d.profit < 0 ? 'bad' : 'good'} />
          <Stat label="Margin" value={d.marginPct === null ? '—' : `${d.marginPct}%`} />
        </div>
        {d.costLines.length ? (
          <div className="overflow-x-auto">
            <T head={['Date', 'Cost', 'From', 'Amount']} rows={d.costLines.map((c) => (
              <tr key={`${c.kind}-${c._id}`} className="border-t border-[#F3F2F2]">
                <td className="px-4 py-2">{fmtDate(c.date)}</td><td className="px-4 py-2">{c.kind === 'bill' ? `Bill ${c.number}` : `Claim ${c.number} · ${c.title}`}</td>
                <td className="px-4 py-2 text-right">{c.party}</td><td className="px-4 py-2 text-right tabular-nums">{money(c.amount, c.currency)}</td>
              </tr>
            ))} />
          </div>
        ) : <p className="text-[12px] text-[#706E6B]">No costs yet. Choose this deal on a vendor bill or an expense claim to count its cost here.</p>}
      </section>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Quotations" count={d.quotations.length} empty="No quotations on this deal.">
          <T head={['Quotation', 'Date', 'Status', 'Total']} rows={d.quotations.map((q) => (
            <tr key={q._id} className="border-t border-[#F3F2F2]"><td className="px-4 py-2 font-semibold">{q.quotationNumber}</td><td className="px-4 py-2">{fmtDate(q.issueDate)}</td><td className="px-4 py-2 text-right"><Pill s={q.status} /></td><td className="px-4 py-2 text-right tabular-nums">{money(q.totalAmount, q.currency)}</td></tr>
          ))} />
        </Section>
        <Section title="Invoices" count={d.invoices.length} empty="No invoices on this deal.">
          <T head={['Invoice', 'Due', 'Status', 'Balance']} rows={d.invoices.map((i) => (
            <tr key={i._id} className="border-t border-[#F3F2F2]"><td className="px-4 py-2 font-semibold">{i.invoiceNumber}</td><td className="px-4 py-2">{fmtDate(i.dueDate)}</td><td className="px-4 py-2 text-right"><Pill s={i.status} /></td><td className="px-4 py-2 text-right tabular-nums">{i.balance ? money(i.balance, i.currency) : '—'}</td></tr>
          ))} />
        </Section>
      </div>
    </div>
  );
}

// Picks a deal to charge a cost to (bills, expense claims).
let dealCache = null;
export function DealSelect({ value, onChange, placeholder = 'No deal' }) {
  const [deals, setDeals] = useState(dealCache || []);
  useEffect(() => {
    if (dealCache) return;
    api.get('/crm/deals', { params: { limit: 500 } })
      .then((r) => { dealCache = (r.data.deals || []).map((x) => ({ _id: x._id, name: x.title })); setDeals(dealCache); })
      .catch(() => setDeals([]));
  }, []);
  if (!deals.length) return null;
  return (
    <div className="flex gap-1 items-center">
      <div className="flex-1"><SearchableSelect value={value || ''} onChange={(v) => onChange(v || null)} options={deals} placeholder={placeholder} /></div>
      {value && <button type="button" className="text-[11px] text-[#706E6B] hover:underline" onClick={() => onChange(null)}>Clear</button>}
    </div>
  );
}

